import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DashboardPage } from "@/pages/DashboardPage";
import { ContactsPage } from "@/pages/ContactsPage";
import { CompaniesPage } from "@/pages/CompaniesPage";
import { DealsPage } from "@/pages/DealsPage";
import { ActivitiesPage } from "@/pages/ActivitiesPage";
import {
	LayoutDashboard, Users, Building2, Handshake, Activity,
	ChevronLeft, ChevronRight,
} from "lucide-react";
import "./index.css";

type Page = "dashboard" | "contacts" | "companies" | "deals" | "activities";

const navItems: { id: Page; label: string; icon: typeof LayoutDashboard }[] = [
	{ id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
	{ id: "contacts", label: "Contacts", icon: Users },
	{ id: "companies", label: "Companies", icon: Building2 },
	{ id: "deals", label: "Deals", icon: Handshake },
	{ id: "activities", label: "Activities", icon: Activity },
];

export function App() {
	const [page, setPage] = useState<Page>("dashboard");
	const [collapsed, setCollapsed] = useState(false);

	return (
		<TooltipProvider>
			<div className="flex h-screen bg-background">
				{/* Sidebar */}
				<aside
					className={`flex flex-col border-r bg-card transition-all duration-200 ${
						collapsed ? "w-16" : "w-56"
					}`}
				>
					<div className="flex items-center gap-2 px-4 h-14 border-b">
						{!collapsed && (
							<>
								<div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
									<span className="text-xs font-bold text-primary-foreground">C</span>
								</div>
								<span className="font-semibold text-sm">CRM</span>
							</>
						)}
						<Button
							variant="ghost"
							size="icon"
							className={`h-8 w-8 ${collapsed ? "mx-auto" : "ml-auto"}`}
							onClick={() => setCollapsed(!collapsed)}
						>
							{collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
						</Button>
					</div>

					<nav className="flex-1 p-2 space-y-1">
						{navItems.map(item => {
							const Icon = item.icon;
							const active = page === item.id;
							return (
								<Button
									key={item.id}
									variant={active ? "secondary" : "ghost"}
									className={`w-full justify-start gap-3 ${
										collapsed ? "px-0 justify-center" : ""
									} ${active ? "font-medium" : "text-muted-foreground"}`}
									onClick={() => setPage(item.id)}
								>
									<Icon className="h-4 w-4 shrink-0" />
									{!collapsed && <span>{item.label}</span>}
								</Button>
							);
						})}
					</nav>

					<Separator />
					<div className="p-3">
						{!collapsed && (
							<p className="text-[10px] text-muted-foreground text-center">
								In-memory data &middot; Resets on restart
							</p>
						)}
					</div>
				</aside>

				{/* Main Content */}
				<main className="flex-1 overflow-y-auto">
					<div className="p-6">
						{page === "dashboard" && <DashboardPage />}
						{page === "contacts" && <ContactsPage />}
						{page === "companies" && <CompaniesPage />}
						{page === "deals" && <DealsPage />}
						{page === "activities" && <ActivitiesPage />}
					</div>
				</main>
			</div>
		</TooltipProvider>
	);
}

export default App;
